<template>
    <div class="sub-modal-container rounded-lg">
        <div class="main-container">
            <PurchaseLicenseHeader @closeDialog="closeDialog" />
            <div class="divider"></div>
            <div class="flex lg:flex-row flex-col items-start gap-9">
                <SelectLicenseType />
                <div class="lg:w-auto w-full">
                    <!-- Upgrade mode: Show upgrade message -->
                    <div v-if="purchaseType === PurchaseType.UPGRADE && license?.slug === selectedPlanType && license?.term === selectedTerm" class="upgrade-right-section">
                        <div class="upgrade-message">
                            <div class="upgrade-message-content">
                                <h3 class="upgrade-title">
                                    Modify Your License
                                </h3>
                                <p class="upgrade-description">
                                    Choose the license that best fits your needs. Your current license will be upgraded to the selected plan.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Purchase mode: Show normal purchase flow -->
                    <div v-else-if="purchaseType === PurchaseType.PURCHASE">
                        <PurchaseLicenseQuantityBlock />
                        <div class="divider-div">
                            <div class="divider"></div>
                        </div>

                        <AddGiftCertificateBlock />
                        <div class="divider-div">
                            <div class="divider"></div>
                        </div>
                        <PurchaseLicenseCostSummary />
                        <!-- <div class="divider-div">
                            <div class="divider"></div>
                        </div> -->
                        <PurchaseLicensesConfirmationBlock />
                    </div>

                    <div v-else-if="purchaseType === PurchaseType.UPGRADE">
                        <PurchaseLicenseCostSummary />
                        <div class="divider-div">
                            <div class="divider"></div>
                        </div>
                        <PurchaseLicensesConfirmationBlock />
                    </div>
                    
                    <!-- Gift mode: Show available licenses for assignment -->
                    <div v-else-if="purchaseType === PurchaseType.GIFT">
                        <AssignLicenseBlock />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { getDialogRef } from '~/composables/dynamic-dialog';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { PurchaseType } from '~/types/licenses/licenses';
import { ModalNames } from '~/types/modals';
import AddGiftCertificateBlock from './Components/AddGiftCertificateBlock.vue';
import AssignLicenseBlock from './Components/AssignLicenseBlock.vue';
import PurchaseLicenseCostSummary from './Components/PurchaseLicenseCostSummary.vue';
import PurchaseLicenseHeader from './Components/PurchaseLicenseHeader.vue';
import PurchaseLicenseQuantityBlock from './Components/PurchaseLicenseQuantityBlock.vue';
import PurchaseLicensesConfirmationBlock from './Components/PurchaseLicensesConfirmationBlock.vue';
import SelectLicenseType from './Components/SelectLicenseType.vue';

const dialogRef = getDialogRef(ModalNames.PURCHASE_LICENSES);

const { purchasingFor, purchaseType, license, selectedPlanType, selectedTerm } = storeToRefs(useSubscribeStore());

// Initialize store by fetching license costs
useSubscribeStore().fetchLicenseCosts();

// Also fetch credit balance when modal is initialized
useSubscribeStore().fetchCreditBalance();


purchasingFor.value = dialogRef.value?.data?.for ?? null;
purchaseType.value = dialogRef.value?.data?.type ?? PurchaseType.PURCHASE;


const personStore = usePersonStore();

if (purchaseType.value === PurchaseType.UPGRADE) { 
    license.value = personStore.person?.licenses.find((license) => license.id === dialogRef.value?.data?.license?.id)!;
    selectedTerm.value = license.value?.term!;
}

// Handle gift purchase - set license count to 1 and purchasingFor to contact ID
if (purchaseType.value === PurchaseType.GIFT && dialogRef.value?.data?.contact) {
    useSubscribeStore().licenseCount = 1;
    purchasingFor.value = dialogRef.value?.data?.for!;
}

const closeDialog = () => {
    dialogRef?.value?.close();
};
</script>

<style lang="scss" scoped>
.sub-modal-container {
    @apply bg-white sm:max-w-[95vw] w-11/12 mx-auto lg:max-w-screen-xl mx-auto overflow-y-auto max-h-screen sm:scale-90 scale-y-95 scale-x-100;
}

.main-container {
    @apply flex sm:p-8 p-4 flex-col gap-8;
}

.divider {
    @apply h-px self-stretch bg-zinc-300;
}

.divider-div {
    @apply border border-zinc-300 w-full border-t my-9;
}

.upgrade-right-section {
    @apply flex flex-col items-center justify-center h-full min-h-[400px] p-8;
}

.upgrade-message {
    @apply border border-blue-300 rounded-lg p-8 max-w-md text-center;

    .upgrade-message-content {
        .upgrade-title {
            @apply font-inter text-xl font-semibold leading-6 mb-3;
        }

        .upgrade-description {
            @apply font-inter text-sm font-normal leading-5 text-zinc-600;
        }
    }
}

.gift-right-section {
    @apply flex flex-col items-center justify-center h-full min-h-[400px] p-8;
}

.gift-message {
    @apply border border-green-300 rounded-lg p-8 max-w-md text-center;

    .gift-message-content {
        .gift-title {
            @apply font-inter text-xl font-semibold leading-6 mb-3;
        }

        .gift-description {
            @apply font-inter text-sm font-normal leading-5 text-zinc-600;
        }
    }
}
</style>
